/**
 * LemonBotHelpButtons — five quick-action buttons for disability-friendly
 * learning support. Each button SETS THE INPUT FIELD to a templated phrase
 * so the student sees what's about to be sent, can edit, and explicitly
 * presses Send. (Never auto-sends.)
 *
 * Used by `<LemonBot />` above the input row.
 *
 * Props
 *   - lessonContext: short string of the current lesson title (optional)
 *   - onPickPrompt(prompt: string): the parent's setter for the input box
 */
import { Sparkles, ListChecks, Volume2, HelpCircle, Footprints } from "lucide-react";

const HELP_ACTIONS = [
  {
    key: "simpler",
    label: "Simpler words",
    icon: Sparkles,
    template: "Can you explain that in simpler words?",
  },
  {
    key: "steps",
    label: "Break into steps",
    icon: ListChecks,
    template: "Break this into small steps for me.",
  },
  {
    key: "read",
    label: "Read it to me",
    icon: Volume2,
    template: "Please read your last answer aloud for me.",
  },
  {
    key: "hint",
    label: "Hint, no answer",
    icon: HelpCircle,
    template: "Help me answer this without giving me the answer.",
  },
  {
    key: "one-step",
    label: "One step at a time",
    icon: Footprints,
    template: "Give me just the next step. One step at a time, please.",
  },
];

export default function LemonBotHelpButtons({ onPickPrompt }) {
  return (
    <div
      className="px-4 pt-3 pb-1 flex flex-wrap gap-1.5 bg-yellow-50/80 border-t border-yellow-100"
      role="group"
      aria-label="Learning support shortcuts"
      data-testid="lemonbot-help-buttons"
    >
      {HELP_ACTIONS.map(({ key, label, icon: Icon, template }) => (
        <button
          key={key}
          type="button"
          onClick={() => onPickPrompt(template)}
          data-testid={`lemonbot-help-${key}`}
          className="inline-flex items-center gap-1 text-[11px] font-semibold bg-white border border-yellow-200 text-yellow-900 rounded-full px-2.5 py-1 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-yellow-400"
          aria-label={`${label}. Inserts: "${template}". Edit and press Send.`}
        >
          <Icon className="w-3 h-3" aria-hidden="true" />
          {label}
        </button>
      ))}
    </div>
  );
}

export { HELP_ACTIONS };
